import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import classNames from 'classnames'
import momentPropTypes from 'react-moment-proptypes'
import { ChevronLeft, ChevronRight, Circle } from 'lucide-react'

import useFacility from '../../../utilities/hooks/useFacility'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import { ruleSetFromRecurrenceRule, levelArrayToString } from '../../../utilities/helpers'

function Calendar({
  className,
  klasses = [],
  timeZone,
  isLoading,
  onMonthChange,
  providedMonth,
  onEventClick = null,
  ...rest
}) {
  useFacility()
  const [currentMonth, setCurrentMonth] = useState(moment(providedMonth).tz(timeZone))

  useEffect(() => {
    setCurrentMonth(providedMonth)
  }, [providedMonth])

  const monthlyOccurrences = useMemo(() => {
    if (!klasses.length || !currentMonth) return new Map()

    const monthStart = moment(currentMonth).tz(timeZone).startOf('month')
    const monthEnd = moment(currentMonth).tz(timeZone).endOf('month')
    const occurrencesByDay = new Map()

    klasses.forEach((klass) => {
      const ruleSet = ruleSetFromRecurrenceRule(klass.recurrenceRule)
      const occurrences = ruleSet.between(monthStart.toDate(), monthEnd.toDate(), true)

      occurrences.forEach((occurrence) => {
        const dayKey = moment(occurrence).tz(timeZone).format('YYYY-MM-DD')
        if (!occurrencesByDay.has(dayKey)) {
          occurrencesByDay.set(dayKey, [])
        }
        occurrencesByDay.get(dayKey).push({
          klass,
          occurrence
        })
      })
    })

    return occurrencesByDay
  }, [klasses, currentMonth, timeZone])

  const goToPreviousMonth = useCallback(() => {
    const newMonth = moment(currentMonth).subtract(1, 'month')
    setCurrentMonth(newMonth)
    onMonthChange(newMonth)
  }, [currentMonth, onMonthChange])

  const goToNextMonth = useCallback(() => {
    const newMonth = moment(currentMonth).add(1, 'month')
    setCurrentMonth(newMonth)
    onMonthChange(newMonth)
  }, [currentMonth, onMonthChange])

  useEffect(() => {
    function handler(event) {
      event.preventDefault()

      const NEXT_KEY = [34, 39]
      const PREVIOUS_KEY = [33, 37]
      const key = event.which

      if (NEXT_KEY.indexOf(key) > -1) {
        goToNextMonth()
        return false
      } else if (PREVIOUS_KEY.indexOf(key) > -1) {
        goToPreviousMonth()
        return false
      }
    }

    document.addEventListener('keydown', handler)

    return () => document.removeEventListener('keydown', handler)
  }, [goToNextMonth, goToPreviousMonth])

  function renderHeader() {
    return (
      <div className="flex items-center justify-between px-2 mb-6 md:mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 md:text-3xl">
          <span>{`${moment(currentMonth).format('MMMM')} `}</span>
          <span className="font-normal text-gray-500">{moment(currentMonth).format('YYYY')}</span>
        </h2>
        <div className="flex items-center gap-2">
          {isLoading && <LoadingSpinner className="w-5 h-5 mr-2 text-gray-600 md:mr-3" />}
          <button
            className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 text-gray-600 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            onClick={goToPreviousMonth}
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button
            className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 text-gray-600 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            onClick={goToNextMonth}
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
    )
  }

  function renderHeaderRow() {
    return [...Array(7)].map((_value, index) => {
      const day = moment().tz(timeZone).startOf('week').add(index, 'days').format('ddd')
      return (
        <div key={day} className="py-3 text-xs font-semibold tracking-wider text-center text-gray-500 uppercase">
          {day}
        </div>
      )
    })
  }

  function handleEventClick(klass, klassDate, event) {
    if (event) {
      event.stopPropagation()
    }
    if (onEventClick) {
      onEventClick(klass.id, klassDate, moment(klassDate).tz(timeZone).format('HH:mm'))
    }
  }

  function handleDayClick(date, eventsForDay) {
    const uniqueKlasses = [...new Map(eventsForDay.map((e) => [e.klass.id, e])).values()]
    if (uniqueKlasses.length === 1) {
      const { klass, occurrence } = uniqueKlasses[0]
      handleEventClick(klass, occurrence, null)
    } else if (uniqueKlasses.length > 1 && onEventClick) {
      onEventClick(
        null,
        date,
        null,
        uniqueKlasses.map((e) => e.klass)
      )
    }
  }

  function renderDays() {
    const monthStart = moment(currentMonth).tz(timeZone).startOf('month')
    const days = []
    let paddingClass

    switch (monthStart.weekday()) {
      case 1:
        paddingClass = 'col-span-1'
        break
      case 2:
        paddingClass = 'col-span-2'
        break
      case 3:
        paddingClass = 'col-span-3'
        break
      case 4:
        paddingClass = 'col-span-4'
        break
      case 5:
        paddingClass = 'col-span-5'
        break
      case 6:
        paddingClass = 'col-span-6'
        break
      default:
        paddingClass = null
    }

    if (paddingClass) {
      days.push(<div key="padding" className={paddingClass}></div>)
    }

    ;[...Array(monthStart.daysInMonth())].forEach((_value, index) => {
      const date = moment(monthStart).tz(timeZone).add(index, 'days')
      const dayKey = date.format('YYYY-MM-DD')
      const eventsForDay = monthlyOccurrences.get(dayKey) || []
      const hasEvents = eventsForDay.length > 0
      const isToday = date.isSame(moment(), 'day')
      const isPast = date.isBefore(moment(), 'day')

      const uniqueKlassIds = new Set(eventsForDay.map((e) => e.klass.id))
      const hasMultipleKlasses = uniqueKlassIds.size > 1

      return days.push(
        <div
          key={date.toString()}
          className={classNames('min-h-[80px] md:min-h-[120px] border border-gray-200 p-1.5 md:p-2 transition-colors', {
            'bg-gray-50': isPast && !isToday,
            'bg-white': !isPast || isToday,
            'cursor-pointer hover:bg-gray-100': hasEvents && hasMultipleKlasses
          })}
          onClick={() => hasEvents && hasMultipleKlasses && handleDayClick(date, eventsForDay)}
          data-cy={date.format('YYYY-MM-DD')}
        >
          <div className="flex items-center justify-between mb-1 md:mb-2">
            <span
              className={classNames(
                'inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 text-xs md:text-sm font-medium rounded-full',
                {
                  'bg-gray-900 text-white': isToday,
                  'text-gray-400': isPast && !isToday,
                  'text-gray-900': !isPast && !isToday
                }
              )}
            >
              {index + 1}
            </span>
            {hasEvents && (
              <div className="flex items-center gap-1">
                <Circle className="w-1.5 h-1.5 md:w-2 md:h-2 text-blue-500 fill-blue-500" />
              </div>
            )}
          </div>

          <div className="space-y-1" data-cy="events">
            {hasEvents &&
              eventsForDay.slice(0, 3).map(({ klass, occurrence }) => (
                <button
                  key={`${klass.id}-${occurrence}`}
                  onClick={(e) => handleEventClick(klass, occurrence, e)}
                  className="block w-full px-1.5 py-1 md:px-2 text-xs font-medium text-left text-gray-900 transition-colors bg-blue-50 border-l-2 border-blue-500 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                >
                  <div className="truncate">
                    {moment(occurrence).tz(timeZone).format('h:mma').replace(':00', '').slice(0, -1)}
                  </div>
                  <div className="hidden text-gray-600 truncate md:block">
                    {levelArrayToString(klass.supportedSkillLevels.map((sl) => sl.name)) || 'All levels'}
                  </div>
                </button>
              ))}
            {hasEvents && eventsForDay.length > 3 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDayClick(date, eventsForDay)
                }}
                className="px-1.5 md:px-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline focus:outline-none"
              >
                +{eventsForDay.length - 3} more
              </button>
            )}
          </div>
        </div>
      )
    })
    return days
  }

  return (
    <div {...rest} className={classNames('w-full', className)}>
      <div className="w-full">
        {renderHeader()}
        <div className="grid grid-cols-7 border-t border-l border-gray-200">{renderHeaderRow()}</div>
        <div className="grid grid-cols-7 border-b border-l border-gray-200">{renderDays()}</div>
      </div>
    </div>
  )
}

Calendar.propTypes = {
  className: PropTypes.string,
  klasses: PropTypes.array,
  timeZone: PropTypes.string,
  isLoading: PropTypes.bool,
  onMonthChange: PropTypes.func,
  onEventClick: PropTypes.func,
  providedMonth: momentPropTypes.momentObj
}

export default Calendar
